/**
 * Dates on which a site has CCTV footage from at least two vehicles.
 * The most recent available dates are checked in one batch request; if that
 * check fails, every date with any footage is offered instead.
 */

import { useQuery } from '@tanstack/react-query'
import { useSiteVideoAvailableDates } from '@/data/hooks/useSiteVideoAvailableDates'
import { CACHE_TIMES } from '@/data/services/api/cacheConfig'
import { siteService } from '@/data/services/domain/siteService'
import { logger } from '@/utils/logger'

const MAX_DATES_TO_CHECK = 30
const MIN_VEHICLE_COUNT = 2

function summarize(dateList) {
  return {
    availableDates: new Set(dateList),
    dateList,
    totalDays: dateList.length,
  }
}

const EMPTY_SUMMARY = summarize([])

export function useMultiVehicleVideoDates(siteId) {
  const dates = useSiteVideoAvailableDates(siteId)
  const dateList = dates.data?.dateList ?? []
  const recentDates = dateList.slice(0, MAX_DATES_TO_CHECK)

  const filtered = useQuery({
    queryKey: ['site', siteId, 'videoDatesWithVehicles', MIN_VEHICLE_COUNT, recentDates],
    queryFn: async () => {
      try {
        const batch = await siteService.getVideoTimelineBatch(siteId, {
          dates: recentDates,
          summary_only: true,
          min_vehicle_count: MIN_VEHICLE_COUNT,
        })
        const matching = (batch.dates ?? [])
          .filter((day) => day.has_video && day.vehicle_count >= MIN_VEHICLE_COUNT)
          .map((day) => day.date)
        if (matching.length > 0) {
          return summarize(matching)
        }
        logger.warn('No dates with multiple vehicles found, offering all available dates')
      } catch (error) {
        logger.warn('Batch video timeline failed, offering all available dates:', error)
      }
      return summarize(dateList)
    },
    enabled: !!siteId && recentDates.length > 0,
    staleTime: CACHE_TIMES.SHORT_LIVED,
    gcTime: CACHE_TIMES.SHORT_CACHE,
    retry: 1,
  })

  const hasNoFootage = dates.data && dateList.length === 0

  return {
    data: hasNoFootage ? EMPTY_SUMMARY : (filtered.data ?? null),
    isLoading: dates.isLoading || filtered.isLoading,
    error: dates.error,
  }
}
